import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { useAdminAuth } from './AdminLayout';
import { toast } from 'sonner';

const STATUSES = ['payment_pending', 'placed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];

export default function AdminOrders() {
  const { password } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const client = adminApi(password);

  const load = () => client.get('/admin/orders').then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    const int = setInterval(load, 8000);
    return () => clearInterval(int);
  }, []);  // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await client.patch(`/admin/orders/${id}/status`, { status });
      toast.success('Status updated');
      load();
    } catch { toast.error('Update failed'); }
  };

  const bypassPayment = async (id, bypassMethod) => {
    try {
      await client.post(`/admin/orders/${id}/bypass-payment`, { bypass_method: bypassMethod });
      toast.success(`Payment bypassed to ${bypassMethod.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bypass failed');
    }
  };

  const filtered = filter === 'all' ? orders : orders.filter((o) => o.status === filter);

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl text-textprimary mb-1">Orders</h1>
      <p className="text-textsecondary font-body text-sm mb-6">Manage & update order status</p>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {['all', ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`admin-filter-${s}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-heading font-semibold ${filter === s ? 'bg-maroon text-white' : 'bg-white text-textsecondary border border-borderbeige'}`}
          >
            {s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && <div className="text-center text-textsecondary py-10 font-body">No orders</div>}
        {filtered.map((o) => (
          <div
            key={o.id}
            data-testid={`admin-order-${o.id}`}
            className={`rounded-2xl p-4 shadow-card border transition-all ${
              o.status === 'payment_pending' ? 'bg-amber-50/20 border-dotted border-amber-500 animate-order-blink' :
              o.status === 'placed' ? 'animate-order-blink border-red-500' :
              o.status === 'preparing' ? 'bg-orange-50/50 border-orange-300' :
              o.status === 'out_for_delivery' ? 'bg-blue-50/55 border-blue-300' :
              o.status === 'cancelled' ? 'bg-red-50/10 border-red-200 opacity-70' :
              'bg-white border-borderbeige'
            }`}
          >
            <div className="flex flex-wrap gap-3 justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-heading font-bold text-textprimary">#{o.id.slice(0, 8).toUpperCase()}</div>
                  {o.status === 'payment_pending' && (
                    <span className="shrink-0 text-[10px] font-heading font-extrabold px-2 py-0.5 rounded-full bg-amber-500 text-white animate-pulse">
                      PAYMENT PENDING
                    </span>
                  )}
                  {o.status === 'placed' && (
                    <span className="shrink-0 text-[10px] font-heading font-extrabold px-2 py-0.5 rounded-full bg-red-600 text-white animate-pulse">
                      NEW ORDER
                    </span>
                  )}
                  {o.status === 'cancelled' && (
                    <span className="shrink-0 text-[10px] font-heading font-extrabold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                      CANCELLED
                    </span>
                  )}
                </div>
                <div className="text-xs text-textsecondary font-body mt-0.5">{new Date(o.created_at).toLocaleString()}</div>
                {o.scheduled_time && (
                  <div className="mt-1.5 text-xs text-amber-800 font-body font-semibold bg-amber-50 border border-amber-250 px-2.5 py-1 rounded-xl w-fit flex items-center gap-1">
                    🕒 Pre-ordered for today: {new Date(o.scheduled_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </div>
                )}
                <div className="text-sm text-textprimary font-body mt-2">{o.customer_name} • {o.customer_phone}</div>
                <div className="text-xs text-textsecondary font-body mt-1">{o.delivery_address}</div>
                <div className="text-xs text-textsecondary font-body mt-2">
                  {o.items.map((i) => `${i.name}×${i.quantity}`).join(', ')}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-maroon text-xl">₹{o.total.toFixed(2)}</div>
                <div className="text-[10px] font-body text-textsecondary uppercase mt-1">{o.payment_method} • {o.payment_status}</div>
                <select
                  value={o.status}
                  onChange={(e) => updateStatus(o.id, e.target.value)}
                  data-testid={`admin-status-select-${o.id}`}
                  className="mt-2 px-3 py-1.5 border border-borderbeige rounded-xl text-sm font-heading font-semibold bg-white"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
            {o.status === 'payment_pending' && (
              <div className="mt-3 flex gap-2 justify-end border-t border-dashed border-borderbeige pt-3">
                <button
                  onClick={() => bypassPayment(o.id, 'cod')}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-xl text-xs font-heading font-semibold transition-colors"
                >
                  Bypass to COD
                </button>
                <button
                  onClick={() => bypassPayment(o.id, 'paid')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-xl text-xs font-heading font-semibold transition-colors"
                >
                  Mark as Paid
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
